import { createContext, useContext, useState, useEffect } from 'react';
import { isTokenExpired, saveToken, removeToken, getToken } from 'utils/auth';
import { jwtDecode } from 'jwt-decode';
import { socket } from 'api';
import { getUserProfile } from 'api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [socketInitialized, setSocketInitialized] = useState(false); // Track socket init

  const initSocket = (userId) => {
    if (!userId) {
      console.error('initSocket: No userId provided, skipping socket initialization');
      return;
    }

    if (socketInitialized) {
      console.log('Socket already initialized for user:', userId);
      socket.emit('joinRoom', userId); // Re-emit joinRoom if needed
      return;
    }

    console.log('Initializing socket for user:', userId);
    socket.connect();

    socket.on('connect', () => {
      console.log('Connected to server:', socket.id);
      socket.emit('joinRoom', userId);
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', socket.id, 'Reason:', reason);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
      console.error('Socket connection error:', err.message);
    });

    socket.on('reconnect', (attempt) => {
      console.log('Reconnected after', attempt, 'attempts');
      socket.emit('joinRoom', userId);
    });

    socket.on('error', ({ message }) => {
      console.error('Socket server error:', message);
    });

    setSocketInitialized(true);
  };

  const refreshUser = async () => {
    try {
      const token = getToken();
      if (!token || isTokenExpired(token)) {
        logout();
        return;
      }
      const decodedToken = jwtDecode(token);
      const profile = await getUserProfile();
      console.log('Refreshed user profile:', JSON.stringify(profile, null, 2));
      const userData = {
        ...profile.user,
        mentorStats: profile.user.mentorStats || null,
        permissions: decodedToken.permissions || [],
        role: decodedToken.role,
        id: decodedToken.id,
      };
      setUser(userData);
      initSocket(userData.id);
    } catch (err) {
      console.error('Failed to refresh user:', err);
      logout();
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = getToken();
      console.log('User Token:', token);


      if (token) {
        if (isTokenExpired(token)) {
          console.warn('Token is expired. Logging out.');
          removeToken();
          setIsAuthenticated(false);
          setUser(null);
        } else {
          const decodedToken = jwtDecode(token);
          setIsAuthenticated(true);

          try {
            const profile = await getUserProfile();
            console.log('Fetched user profile:', JSON.stringify(profile, null, 2));
            const userData = {
              ...profile.user,
              mentorStats: profile.user.mentorStats || null,
              permissions: decodedToken.permissions || [],
              role: decodedToken.role,
              id: decodedToken.id,
            };
            setUser(userData);
            initSocket(userData.id);
          } catch (err) {
            console.error('Failed to fetch profile:', err);
            setUser({
              id: decodedToken.id,
              role: decodedToken.role,
              permissions: decodedToken.permissions || [],
              phoneVerified: false,
            });
            initSocket(decodedToken.id);
          }
        }
      } else {
        console.warn('No token found. User is not authenticated.');
        setIsAuthenticated(false);
        setUser(null);
        socket.disconnect(); // Ensure socket is disconnected
      }

      setLoading(false);
    };

    initializeAuth();

    return () => {
      socket.disconnect();
      setSocketInitialized(false);
      console.log('Socket disconnected on cleanup');
    };
  }, []);

  const login = async (token, rememberMe) => {
    saveToken(token, rememberMe);
    const decodedToken = jwtDecode(token);
    setIsAuthenticated(true);

    try {
      const profile = await getUserProfile();
      console.log('Fetched user profile on login:', JSON.stringify(profile, null, 2));
      const userData = {
        ...profile.user,
        mentorStats: profile.user.mentorStats || null,
        permissions: decodedToken.permissions || [],
        role: decodedToken.role,
        id: decodedToken.id,
      };
      setUser(userData);
      initSocket(userData.id);
      const redirectUrl = localStorage.getItem('redirectAfterLogin') || '/admin/dashboard';
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectUrl);
      const timeUntilExpiration = decodedToken.exp * 1000 - Date.now();
      if (timeUntilExpiration > 0) {
        setTimeout(() => logout(true), timeUntilExpiration);
      }
    } catch (err) {
      console.error('Failed to fetch profile after login:', err);
      removeToken();
      setIsAuthenticated(false);
      setUser(null);
      navigate('/auth/sign-in');
      throw new Error('Login failed: Unable to fetch user profile.');
    }
  };

  const logout = (storeRedirect = false) => {
    console.log('Logging out user');
    if (storeRedirect) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
    }
    removeToken();
    setIsAuthenticated(false);
    setUser(null);
    socket.disconnect();
    setSocketInitialized(false);
    console.log('Socket disconnected on logout');
    navigate('/auth/sign-in');
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, login, logout, loading, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  console.log('useAuth context:', context);
  return context;
};